import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, QrCode, LifeBuoy, Receipt as RIcon, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Receipt } from "@/components/Payment";
import { api, fmtErr, peso, dt, dur, label } from "@/lib/api";

const PAY = [["gcash", "GCash", "bg-blue-600"], ["maya", "Maya", "bg-emerald-600"], ["qrph", "QR Ph", "bg-slate-800"], ["credit_card", "Card", "bg-amber-600"]];

export default function Portal() {
  const { code } = useParams();
  const nav = useNavigate();
  const [input, setInput] = useState(code || "");
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rcpt, setRcpt] = useState(null);
  const [help, setHelp] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(async (c) => {
    try {
      setS((await api.get(`/portal/session/${c}`)).data);
    } catch (e) {
      setS(null);
      toast.error(fmtErr(e));
    }
  }, []);
  useEffect(() => {
    if (code) load(code);
  }, [code, load]);
  const pay = async (m) => {
    setBusy(true);
    try {
      const { data } = await api.post("/portal/pay", { ticket_code: s.ticket_code, method: m });
      if (data.payment.status === "successful") {
        toast.success("Payment successful");
        setRcpt((await api.get(`/portal/receipt/${data.payment.receipt_no}`, { params: { ticket: s.ticket_code } })).data);
      } else toast.error("Payment failed. Please try again.");
      load(s.ticket_code);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };
  const assist = async () => {
    try {
      const { data } = await api.post("/portal/assist", { ticket_code: s?.ticket_code, message: msg });
      toast.success(`Help is on the way. Ref ${data.reference}`);
      setHelp(false);
      setMsg("");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <div className="min-h-screen bg-[#0B0F17] px-4 py-8 text-white">
      <div className="mx-auto max-w-md">
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-amber-500 font-mono text-lg font-black text-slate-950">P</div>
          <div><div className="font-display font-extrabold">PARKINGCORE</div><div className="text-xs text-slate-400">Customer Portal</div></div>
        </div>
        {!s && (
          <div className="fade-up space-y-4" data-testid="portal-lookup">
            <QrCode className="h-14 w-14 text-amber-500" />
            <h1 className="font-display text-3xl font-extrabold">Scan or enter your ticket code</h1>
            <p className="text-sm text-slate-400">The code is printed under the QR on your parking ticket.</p>
            <Input data-testid="portal-code-input" className="h-14 border-slate-700 bg-slate-900 text-center font-mono text-2xl uppercase tracking-widest" value={input} onChange={(e) => setInput(e.target.value.toUpperCase())} placeholder="1A2B3C4D" />
            <Button data-testid="portal-lookup-btn" className="h-12 w-full bg-amber-500 text-base font-bold text-slate-950 hover:bg-amber-400" disabled={!input} onClick={() => nav(`/portal/${input.trim()}`)}>View my parking</Button>
          </div>
        )}
        {s && !rcpt && (
          <div className="fade-up space-y-5" data-testid="portal-session">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
              <div className="flex items-center justify-between"><span className="plate border-slate-500 bg-slate-800 text-lg">{s.plate}</span><span className="rounded bg-slate-800 px-2 py-0.5 text-xs uppercase">{label(s.status)}</span></div>
              <div className="mt-2 text-sm text-slate-400">{s.branch_name}</div>
              <div className="mt-5 grid grid-cols-2 gap-4">
                <div><div className="text-xs uppercase text-slate-500">Entered</div><div className="font-mono">{dt(s.entry_time)}</div></div>
                <div><div className="text-xs uppercase text-slate-500">Duration</div><div className="font-mono" data-testid="portal-duration">{dur(s.duration_min)}</div></div>
              </div>
              <div className="mt-5 border-t border-slate-800 pt-4">
                <div className="text-xs uppercase text-slate-500">Amount due</div>
                <div className="font-mono text-4xl font-bold text-amber-500" data-testid="portal-amount">{peso(s.fee.balance)}</div>
                {s.fee.amount_paid > 0 && <div className="text-xs text-emerald-400">Paid {peso(s.fee.amount_paid)} — exit within 15 minutes</div>}
              </div>
            </div>
            {s.fee.balance > 0 && !["completed", "cancelled"].includes(s.status) && (
              <div className="grid grid-cols-2 gap-3">
                {PAY.map(([m, l, c]) => (
                  <button key={m} data-testid={`portal-pay-${m}`} disabled={busy} onClick={() => pay(m)} className={`${c} flex h-16 items-center justify-center rounded-xl text-lg font-bold transition-transform active:scale-95 disabled:opacity-50`}>
                    {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : l}
                  </button>
                ))}
              </div>
            )}
            {s.payments?.map((p) => (
              <button key={p.id} data-testid={`portal-receipt-${p.receipt_no}`} onClick={async () => setRcpt((await api.get(`/portal/receipt/${p.receipt_no}`, { params: { ticket: s.ticket_code } })).data)}
                className="flex w-full items-center justify-between rounded-lg border border-slate-800 px-4 py-3 text-sm"><span className="flex items-center gap-2"><RIcon className="h-4 w-4" />{p.receipt_no}</span><span className="font-mono">{peso(p.amount)}</span></button>
            ))}
            <p className="text-center text-[11px] text-slate-500">Payments are processed by a SIMULATED gateway in this demo.</p>
          </div>
        )}
        {rcpt && (
          <div className="fade-up space-y-3">
            <Receipt data={rcpt} />
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="border-slate-700 bg-transparent" onClick={() => window.print()} data-testid="portal-print"><Printer className="h-4 w-4" /> Save / print</Button>
              <Button className="bg-amber-500 text-slate-950" data-testid="portal-back" onClick={() => setRcpt(null)}>Done</Button>
            </div>
          </div>
        )}
        <div className="mt-8">
          {!help ? (
            <button data-testid="portal-help-btn" onClick={() => setHelp(true)} className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-800 py-3 text-sm text-slate-300"><LifeBuoy className="h-4 w-4" /> Request assistance</button>
          ) : (
            <div className="space-y-2" data-testid="portal-help-form">
              <Textarea data-testid="portal-help-msg" className="border-slate-700 bg-slate-900" placeholder="How can we help?" value={msg} onChange={(e) => setMsg(e.target.value)} />
              <Button data-testid="portal-help-send" className="w-full" disabled={!msg} onClick={assist}>Send</Button>
            </div>
          )}
          {s && <button className="mt-4 w-full text-center text-xs text-slate-500 underline" onClick={() => { setS(null); setRcpt(null); nav("/portal"); }} data-testid="portal-other">Look up another ticket</button>}
        </div>
      </div>
    </div>
  );
}
